import { type ReactNode, useSyncExternalStore } from 'react'

/**
 * The imperative toast store — a module-level singleton so `toast.*` is callable from anywhere
 * (contexts, effects, event handlers), not only from a component subscribed to a provider. This is
 * the same shape the app used to import from `sonner`; swapping the import is the whole migration.
 *
 * `<Toaster>` subscribes via {@link useToasts} and renders the list. Auto-dismiss timers live here
 * (not in React) so they survive re-renders, can be deduped by id, and can be paused while a toast
 * is hovered or dragged (see {@link pauseToastTimers} / {@link resumeToastTimers}).
 */

/**
 * Status drives the icon chip's colour + glyph. `neutral` is the plain bell (bare `toast()`); the
 * rest map to the semantic helpers (`toast.success`, `.error`, `.info`, `.loading`).
 */
export enum ToastStatus {
  Neutral = 'neutral',
  Info = 'info',
  Loading = 'loading',
  Success = 'success',
  Error = 'error'
}

/** An action button rendered under the message. `onClick` receives the toast id so it can self-dismiss. */
export interface ToastAction {
  label: ReactNode
  onClick: (id: string) => void
}

/** Options accepted by every `toast.*` call — the slice of sonner's option bag Parsley actually uses. */
export interface ToastOptions {
  /** Stable id. Re-firing with the same id replaces the live toast in place (dedup) and restarts its timer. */
  id?: string
  /** Secondary line under the title. */
  description?: ReactNode
  /** Optional action button. */
  action?: ToastAction
  /** Auto-dismiss delay in ms. `0` or `Infinity` keeps it until dismissed. Defaults to {@link DEFAULT_DURATION}. */
  duration?: number
  /** Set `false` to hide the close affordance and disable swipe-to-dismiss. */
  dismissible?: boolean
  /** Custom glyph shown in the chip instead of the status icon (e.g. a contextual Coffee/Moon). */
  icon?: ReactNode
}

/** A live toast in the store. `title` + `status` are always present; timing fields are resolved on add. */
export interface ParsleyToast {
  id: string
  title: ReactNode
  description?: ReactNode
  status: ToastStatus
  action?: ToastAction
  duration: number
  dismissible: boolean
  createdAt: number
  icon?: ReactNode
}

/** The callable `toast` singleton: `toast(msg, opts)` plus semantic helpers and `dismiss`. */
export interface ToastFn {
  (message: ReactNode, options?: ToastOptions): string
  success: (message: ReactNode, options?: ToastOptions) => string
  error: (message: ReactNode, options?: ToastOptions) => string
  info: (message: ReactNode, options?: ToastOptions) => string
  loading: (message: ReactNode, options?: ToastOptions) => string
  /** Dismiss one toast by id, or all toasts when called with no argument. */
  dismiss: (id?: string) => void
}

/** Default auto-dismiss window — long enough to read a line and reach for an action. */
export const DEFAULT_DURATION = 4200
/** Cap on simultaneously-tracked toasts; the oldest are dropped past this. */
const LIMIT = 5

// --- external store ---------------------------------------------------------

let toasts: ParsleyToast[] = []
const listeners = new Set<() => void>()

function emit() {
  for (const listener of listeners) listener()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function getSnapshot(): ParsleyToast[] {
  return toasts
}

// Stable empty reference for SSR — a fresh array each call would loop useSyncExternalStore.
const EMPTY: ParsleyToast[] = []
function getServerSnapshot(): ParsleyToast[] {
  return EMPTY
}

// --- timers (with pause-on-interaction) -------------------------------------

interface TimerEntry {
  /** Milliseconds left when the timer was last (re)started. */
  remaining: number
  /** Wall-clock at last (re)start, for computing elapsed on pause. */
  startedAt: number
  handle: ReturnType<typeof setTimeout> | null
}

const timers = new Map<string, TimerEntry>()
let paused = false

function scheduleTimer(id: string, duration: number) {
  clearTimer(id)
  if (!Number.isFinite(duration) || duration <= 0) return
  const handle = paused ? null : setTimeout(() => dismiss(id), duration)
  timers.set(id, { remaining: duration, startedAt: Date.now(), handle })
}

function clearTimer(id: string) {
  const entry = timers.get(id)
  if (entry?.handle) clearTimeout(entry.handle)
  timers.delete(id)
}

/**
 * Pause every running timer — called while any toast is hovered or being dragged, so a toast never
 * times out while the user is reading it or reaching for its action. Idempotent.
 */
export function pauseToastTimers() {
  if (paused) return
  paused = true
  const now = Date.now()
  timers.forEach((entry) => {
    if (entry.handle) {
      clearTimeout(entry.handle)
      entry.handle = null
    }
    entry.remaining = Math.max(0, entry.remaining - (now - entry.startedAt))
  })
}

/** Resume every paused timer with the time it had left. Idempotent. */
export function resumeToastTimers() {
  if (!paused) return
  paused = false
  const now = Date.now()
  timers.forEach((entry, id) => {
    entry.startedAt = now
    entry.handle = setTimeout(() => dismiss(id), entry.remaining)
  })
}

// --- mutations --------------------------------------------------------------

let seq = 0
function nextId(): string {
  seq += 1
  return `toast-${Date.now().toString(36)}-${seq}`
}

function add(message: ReactNode, status: ToastStatus, options: ToastOptions = {}): string {
  const id = options.id ?? nextId()
  const duration = options.duration ?? (status === ToastStatus.Loading ? Number.POSITIVE_INFINITY : DEFAULT_DURATION)
  const record: ParsleyToast = {
    id,
    title: message,
    description: options.description,
    status,
    action: options.action,
    duration,
    dismissible: options.dismissible ?? true,
    createdAt: Date.now(),
    icon: options.icon
  }

  const idx = toasts.findIndex((t) => t.id === id)
  if (idx >= 0) {
    // Dedup: replace the live toast in place so its stack position is preserved.
    const next = toasts.slice()
    next[idx] = record
    toasts = next
  } else {
    const combined = [...toasts, record]
    const overflow = combined.length - LIMIT
    for (let i = 0; i < overflow; i++) clearTimer(combined[i].id)
    toasts = combined.slice(-LIMIT)
  }

  scheduleTimer(id, duration)
  emit()
  return id
}

function dismiss(id?: string) {
  if (id === undefined) {
    timers.forEach((_, key) => {
      clearTimer(key)
    })
    toasts = []
    emit()
    return
  }
  const next = toasts.filter((t) => t.id !== id)
  if (next.length === toasts.length) return
  clearTimer(id)
  toasts = next
  emit()
}

export const toast: ToastFn = Object.assign((message: ReactNode, options?: ToastOptions) => add(message, ToastStatus.Neutral, options), {
  success: (message: ReactNode, options?: ToastOptions) => add(message, ToastStatus.Success, options),
  error: (message: ReactNode, options?: ToastOptions) => add(message, ToastStatus.Error, options),
  info: (message: ReactNode, options?: ToastOptions) => add(message, ToastStatus.Info, options),
  loading: (message: ReactNode, options?: ToastOptions) => add(message, ToastStatus.Loading, options),
  dismiss: (id?: string) => dismiss(id)
})

/** Subscribe a component to the live toast list. Used by `<Toaster>`. */
export function useToasts(): ParsleyToast[] {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
}
